import { createStore } from "vuex";
import { yyyymmdd } from "utils";

import {
  RECEIVE_REPORTS,
  RECEIVE_ADDITIONAL_INFO,
  RECEIVE_REPORTS_FOR_LIST,
  UPDATE_REPORTS,
  APPLY_FILTERING,
  SAVE_FILTERING_PARAMS,
  CLEAR_FILTERING,
  PASS_STATES,
} from "../constants";

export const store = createStore({
  state: {
    reports: [],
    reportsFiltered: [],
    reportsList: [],
    reportsListCount: 0,
    programs: [],
    selectedBP: null,
    manager: false,
    reportLatestModified: null,
    searchPhrase: "",
    selectedDates: [],
    sortParams: [],
    states: [],
    hideParam: true,
    severityParam: "",
    stateParam: "",
    newReports: 0,
    slaOneDay: 0,
    slaActionRequired: 0,
    selectedRadio: "show",
    program_labels: [],
    labelList: [],
    totalItems: 0,
  },

  getters: {
    getTotalItems: (state) => {
      return state.totalItems;
    },
    getReportsForSearch: (state) => {
      return state.reports || [];
    },
    getNewReportsCount: (state) => {
      return state.newReports;
    },
    getSlaOneDay: (state) => {
      return state.slaOneDay;
    },
    getIsManager: (state) => {
      return state.manager;
    },
    getSlaActionRequired: (state) => {
      return state.slaActionRequired;
    },
    reportsMapped: (state) => {
      const mapped = {};
      state.states.forEach((reportsState) => {
        mapped[reportsState] = [];
      });

      state.reportsFiltered.forEach((report) => {
        mapped[report.state] = [...mapped[report.state], report];
      });

      if (state.reportLatestModified) {
        const stateRow = mapped[state.reportLatestModified.reportState];
        const report = stateRow.find((report) =>
          report.id === state.reportLatestModified.id ? report : null
        );

        stateRow.splice(stateRow.indexOf(report), 1);
        stateRow.splice(state.reportLatestModified.index, 0, report);

        mapped[state.reportLatestModified.reportState] = stateRow;
        state.reportLatestModified = null;
      }

      return mapped;
    },
    getReportsListPagesCount: (state) => {
      return Math.ceil(state.reportsListCount / 10);
    },

    getReportCount: (state) => {
      return Math.ceil(state.reportsListCount);
    },
  },

  mutations: {
    [RECEIVE_REPORTS](
      state,
      { reports, count, programs, manager, program_labels, total_items }
    ) {
      state.reports = reports;
      state.reportsFiltered = reports;
      state.reportsListCount = count;
      state.labelList = program_labels;
      state.totalItems = total_items;
    },
    [PASS_STATES](state, passedStates) {
      state.states = passedStates;
    },
    [RECEIVE_REPORTS_FOR_LIST](
      state,
      {
        reports,
        count,
        new_reports,
        sla_one_day,
        sla_action_required,
        program_labels,
        total_items,
      }
    ) {
      state.reportsList = reports;
      state.reportsListCount = count;
      state.newReports = new_reports;
      state.slaOneDay = sla_one_day;
      state.slaActionRequired = sla_action_required;
      state.labelList = program_labels;
      state.totalItems = total_items;
    },
    [RECEIVE_ADDITIONAL_INFO](state, { programs, manager, bp }) {
      state.programs = programs.map(({ title, ...rest }) => ({
        ...rest,
        label: title,
      }));
      state.manager = manager;

      state.selectedBP = state.programs.find((program) => program.id === bp);
    },

    [SAVE_FILTERING_PARAMS](
      state,
      { searchPhrase, selectedDates, sortParams }
    ) {
      state.searchPhrase = searchPhrase;
      state.selectedDates = [selectedDates[0], selectedDates[1]];
      state.sortParams = sortParams;
      // state.selectedSeverityItems = selectedSeverityItems;
    },

    [APPLY_FILTERING](state, { search }) {
      let temporary = search;

      const start = Date.parse(state.selectedDates[0]);
      const end = Date.parse(state.selectedDates[1]);

      if (!isNaN(start) && !isNaN(end)) {
        temporary = search.filter(
          ({ created }) =>
            Date.parse(created) >= start && Date.parse(created) <= end
        );
      }
      state.reportsFiltered = temporary;
    },
    [CLEAR_FILTERING](state) {
      state.reportsFiltered = state.reports;

      state.searchPhrase = "";
      state.selectedDates = [];
    },

    [UPDATE_REPORTS](state, { oldState, updatedReport, index }) {
      state.reportLatestModified = {
        id: updatedReport.id,
        reportState: updatedReport.state,
        index,
      };

      state.reportsFiltered = state.reportsFiltered.map((report) =>
        report.id === updatedReport.id ? updatedReport : report
      );
    },
  },

  actions: {
    async RECEIVE_REPORTS({ commit, state }) {
      const searchParam = state.searchPhrase
        ? `&search=${state.searchPhrase}`
        : "";
      const sortParams = state.sortParams[0]
        ? `&sort_by=${state.sortParams[0]}&mode=${state.sortParams[1]}`
        : "";
      const hideParam = state.hideParam
        ? `&hide_closed=${state.hideParam}`
        : "";
      const severityParam = state.severityParam
        ? `&filter_by_severity=${state.severityParam}`
        : "";
      const labelParam = state.labelParam
        ? `&filter_by_label=${state.labelParam}`
        : "";
      let stateParam;
      if (state.stateParam != "") {
        stateParam = `&filter_by_state=${state.stateParam}`;
      } else {
        stateParam = "";
      }

      let startDate = null;
      let endDate = null;

      if (state.selectedDates[0] && state.selectedDates[1]) {
        startDate = yyyymmdd(state.selectedDates[0]);
        endDate = yyyymmdd(state.selectedDates[1]);
      }

      const selectedDates =
        startDate && endDate ? `&created_at=≥${startDate}|≤${endDate}` : "";

      const response = await axios.get(
        `grid_reports?${searchParam}${sortParams}${selectedDates}${hideParam}${severityParam}${stateParam}${labelParam}${labelParam}`
      );

      commit("RECEIVE_REPORTS", response.data);

      if (response.data.programs) {
        commit("RECEIVE_ADDITIONAL_INFO", {
          programs: response.data.programs,
          manager: response.data.manager,
          bp: response.data.current_bounty_program,
        });
      }
    },

    async RECEIVE_REPORT({}, id) {
      const response = await axios.get(`reports/${id}.json`);

      return response.data;
    },

    async RECEIVE_REPORTS_FOR_LIST({ dispatch, commit, state }, page = 1) {
      const searchParam = state.searchPhrase
        ? `&search=${state.searchPhrase}`
        : "";
      const sortParams = state.sortParams[0]
        ? `&sort_by=${state.sortParams[0]}&mode=${state.sortParams[1]}`
        : "";
      const hideParam = state.hideParam
        ? `&hide_closed=${state.hideParam}`
        : "";
      const severityParam = state.severityParam
        ? `&filter_by_severity=${state.severityParam}`
        : "";
      const labelParam = state.labelParam
        ? `&filter_by_label=${state.labelParam}`
        : "";
      let stateParam;
      if (state.stateParam != "") {
        stateParam = `&filter_by_state=${state.stateParam}`;
      } else {
        stateParam = "";
      }

      let startDate = null;
      let endDate = null;

      if (state.selectedDates[0] && state.selectedDates[1]) {
        startDate = yyyymmdd(state.selectedDates[0]);
        endDate = yyyymmdd(state.selectedDates[1]);
      }

      const selectedDates =
        startDate && endDate ? `&created_at=≥${startDate}|≤${endDate}` : "";

      const response = await axios.get(
        `list_reports?${searchParam}${sortParams}${selectedDates}${hideParam}${severityParam}${stateParam}${labelParam}&page=${page}`
      );
      commit("RECEIVE_REPORTS_FOR_LIST", response.data);

      if (response.data.programs) {
        commit("RECEIVE_ADDITIONAL_INFO", {
          programs: response.data.programs,
          manager: response.data.manager,
          bp: response.data.current_bounty_program,
        });
      }
    },

    async REPORT_UPDATE_STATE(
      { getters, commit, state },
      { report, reportsState }
    ) {
      const reportPayload = report.payload;
      const index = report.addedIndex;
      if (reportsState === "paid") {
        window.location.href = `reports/payout/${reportPayload.id}`;
      } else {
        const response = await axios.put(
          `/manager/company/bounty_programs/${reportPayload.bounty_program_id.$oid}/reports/${reportPayload.id}/workflow?state=to_${reportsState}`
        );
      }

      commit("UPDATE_REPORTS", {
        oldState: reportPayload.state,
        updatedReport: response.data,
        index,
      });
    },
  },
});
